import { SearchOutlined } from '@ant-design/icons'
import { Badge, Button, Input } from 'antd'
import { useMergeState } from 'common/hooks/useMergeState'
import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { colorPresets, FilterTags } from './filterTags'
import { getCategories, getIndividuals, getIndustries, getTechnologies } from './lib'
import SelectMenuOptions from './selectMenuOptions'

const checkLength = str => (str && str.length > 0 ? 1 : 0)
const checkArrayLength = arr => (arr ? arr.length : 0)

const selectorsFiltersEnabled = ({ category, technology, individual, industry }) =>
  checkArrayLength(category) +
  checkArrayLength(technology) +
  checkArrayLength(individual) +
  checkArrayLength(industry)

const allFiltersEnabled = ({ category, technology, individual, industry, title }) =>
  selectorsFiltersEnabled({ category, technology, individual, industry }) +
  checkLength(title)

const initialFilterOptionsObjects = {
  loading: false,
  categories: [],
  technologies: [],
  individuals: [],
  industries: []
}

const DEBOUNCE_TIMER = 300

const noTagsFilters = ['name', 'title']

// Helper function to create selector configuration
const createSelectorConfig = (type, options, value, label = null, setValue) => ({
  type,
  value,
  options,
  setValue,
  ...(label && { label })
})

// Helper function to build selectors array
const buildSelectors = (
  filtersOptions,
  searchConfig,
  selectedFilters,
  createSelector
) => {
  const selectors = []
  const { categories, technologies, individuals, industries } = filtersOptions
  const {
    categorySearch,
    technologySearch,
    individualSearch,
    industrySearch
  } = searchConfig

  if (categorySearch && categories) {
    selectors.push(createSelector('category', categories, selectedFilters.category))
  }

  if (technologySearch && technologies) {
    selectors.push(createSelector('technology', technologies, selectedFilters.technology))
  }

  if (individualSearch && individuals) {
    selectors.push(
      createSelector('individual', individuals, selectedFilters.individual, 'author')
    )
  }

  if (industrySearch && industries) {
    selectors.push(createSelector('industry', industries, selectedFilters.industry))
  }

  return selectors
}

// Helper function to fetch all filter options
const fetchAllFilterOptions = async searchConfig => {
  const {
    categorySearch,
    technologySearch,
    individualSearch,
    industrySearch
  } = searchConfig

  const categories = categorySearch ? await getCategories() : []
  const technologies = technologySearch ? await getTechnologies() : []
  const individuals = individualSearch ? await getIndividuals() : []
  const industries = industrySearch ? await getIndustries() : []

  return {
    loading: false,
    categories,
    technologies,
    individuals,
    industries
  }
}

// Custom hook for filter management
const useFilterManagement = (filterBar, { selectedFilters, handleChangeFilters }) => {
  const { categorySearch, individualSearch, technologySearch, industrySearch } = filterBar
  const [filtersOptions, setFiltersOptions] = useMergeState(initialFilterOptionsObjects)
  const [searchValue, setSearchValue] = useState('')

  const handleChangeSelectors = useCallback(
    selector => {
      handleChangeFilters(selector)
    },
    [handleChangeFilters]
  )

  const createSelector = useCallback(
    (type, options, value, label = null) =>
      createSelectorConfig(type, options, value, label, handleChangeSelectors),
    [handleChangeSelectors]
  )

  const selectorsObject = useMemo(() => {
    const searchConfig = {
      categorySearch,
      technologySearch,
      individualSearch,
      industrySearch
    }
    return buildSelectors(filtersOptions, searchConfig, selectedFilters, createSelector)
  }, [
    filtersOptions,
    categorySearch,
    technologySearch,
    individualSearch,
    industrySearch,
    selectedFilters,
    createSelector
  ])

  const handleOnChangeInput = useCallback(
    event => {
      handleChangeFilters({ title: event.target.value })
    },
    [handleChangeFilters]
  )

  const debouncedChangeHandler = useMemo(
    () => debounce(handleOnChangeInput, DEBOUNCE_TIMER),
    [handleOnChangeInput]
  )

  const changeHandler = useCallback(
    event => {
      setSearchValue(event.target.value)
      debouncedChangeHandler(event)
    },
    [setSearchValue, debouncedChangeHandler]
  )

  const resetFilters = useCallback(() => {
    setSearchValue('')
    handleChangeFilters({
      title: '',
      category: [],
      technology: [],
      individual: [],
      industry: []
    })
  }, [handleChangeFilters])

  const removeFilter = useCallback(
    (key, filterId) => {
      const newFilters = {
        [key]: selectedFilters[key].filter(id => id !== filterId)
      }
      handleChangeFilters({ ...selectedFilters, ...newFilters })
    },
    [selectedFilters, handleChangeFilters]
  )

  useEffect(() => {
    const fetchFiltersOptions = async () => {
      setFiltersOptions({ loading: true })
      const searchConfig = {
        categorySearch,
        technologySearch,
        individualSearch,
        industrySearch
      }
      const options = await fetchAllFilterOptions(searchConfig)
      setFiltersOptions(options)
    }
    fetchFiltersOptions()
  }, [
    categorySearch,
    technologySearch,
    individualSearch,
    industrySearch,
    setFiltersOptions
  ])

  return {
    filtersOptions,
    searchValue,
    selectorsObject,
    changeHandler,
    resetFilters,
    removeFilter
  }
}

const FilterBar = ({ filterBar, filters }) => {
  const { selectedFilters } = filters
  const inputRef = useRef(null)
  const {
    filtersOptions,
    searchValue,
    selectorsObject,
    changeHandler,
    resetFilters,
    removeFilter
  } = useFilterManagement(filterBar, filters)

  return (
    <span className="filter-bar-wrapper">
      <div className="filter-bar-container">
        <Input
          className="filter-bar-input"
          id="title-input"
          placeholder="Search..."
          value={searchValue}
          onChange={changeHandler}
          size="large"
          prefix={<SearchOutlined className="filter-bar-search-icon" />}
          ref={inputRef}
          allowClear
        />
        <span className="filters-container">
          {selectorsObject.map((selector, index) => (
            <Badge
              key={selector.type}
              count={selector.value.length}
              className="filter-bar-selector"
              color={colorPresets[index]}
            >
              <SelectMenuOptions
                type={selector.type}
                label={selector.label}
                hideLabel
                placeholder={selector.label ?? selector.type}
                value={selector.value}
                options={selector.options}
                setValue={selector.setValue}
                maxTagCount={0}
                maxTagPlaceholder={() => <div>{selector.label ?? selector.type}</div>}
              />
            </Badge>
          ))}
          <Button
            type="link"
            onClick={resetFilters}
            className={
              allFiltersEnabled(selectedFilters) === 0
                ? 'clear-button hide-clear-button'
                : 'clear-button'
            }
          >
            Clear
          </Button>
        </span>
      </div>
      {selectorsFiltersEnabled(selectedFilters) > 0 && (
        <div className="filter-tags">
          <FilterTags
            selectedFilters={selectedFilters}
            filtersOptions={filtersOptions}
            removeFilter={removeFilter}
            excludedFilters={noTagsFilters}
          />
        </div>
      )}
    </span>
  )
}

export default FilterBar
